import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { SurveyDatabase } from '../local-db/survey.database';
import { HttpHelperService } from './http-helper.service';
import { NetworkConnectivityService } from './network-connectivity.service';
import { environment } from 'src/environments/environment';
import { CaseConverter } from '../helpers/case-converter.helper';
import {
  FlushPendingResult,
  FlushProgress,
  PendingSurveySubmission,
  SurveyAnswerResult,
} from '../entities/survey.entity';
import { isKioskModeEnabled } from '../helpers/kiosk-mode.helper';

const LAST_SYNC_STORAGE_KEY = 'surveyOfflineLastSyncAt';
const TRANSIENT_SUBMIT_STATUSES = [0, 408, 429, 502, 503, 504];

export function isTransientSubmitError(error: unknown): boolean {
  if (!error) {
    return true;
  }

  if (error instanceof HttpErrorResponse) {
    return TRANSIENT_SUBMIT_STATUSES.includes(error.status);
  }

  if (typeof error === 'object') {
    const status = (error as { status?: number }).status;
    if (status !== undefined && TRANSIENT_SUBMIT_STATUSES.includes(status)) {
      return true;
    }
    const message = (error as { message?: string }).message;
    if (message === 'Unknown Error' || message === 'Http failure response') {
      return true;
    }
    if ((error as { error?: unknown }).error instanceof ProgressEvent) {
      return true;
    }
  }

  return false;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyOfflineSyncService {
  private readonly baseUrl = environment.endpoint;
  private readonly submitTimeoutMs = 30000;
  private readonly maxRetriesPerItem = 3;
  private readonly retryBaseDelayMs = 2000;
  private readonly delayBetweenSubmissionsMs = 1500;
  private readonly maxConsecutiveFailures = 3;
  private readonly minMultiplyFactor = 2;
  private readonly maxMultiplyFactor = 500;
  private readonly _pendingCount = new BehaviorSubject<number>(0);
  readonly pendingCount$ = this._pendingCount.asObservable();
  private readonly _lastSyncAt = new BehaviorSubject<number | null>(
    this.readLastSyncAt()
  );
  readonly lastSyncAt$ = this._lastSyncAt.asObservable();
  private readonly _flushProgress = new BehaviorSubject<FlushProgress>({
    total: 0,
    synced: 0,
    failed: 0,
    remaining: 0,
    phase: 'complete',
  });
  readonly flushProgress$ = this._flushProgress.asObservable();

  get flushProgress(): FlushProgress {
    return this._flushProgress.value;
  }
  private flushPromise: Promise<FlushPendingResult> | null = null;
  private pauseRequested = false;
  private readonly initPromise: Promise<void>;

  constructor(
    private db: SurveyDatabase,
    private httpHelperService: HttpHelperService,
    private networkConnectivity: NetworkConnectivityService
  ) {
    this.initPromise = this.initializeQueue();
  }

  get lastSyncAt(): number | null {
    return this._lastSyncAt.value;
  }

  async queueSubmission(payload: SurveyAnswerResult): Promise<void> {
    await this.initPromise;

    const entry: PendingSurveySubmission = {
      eventUUID: payload.eventUUID,
      payload,
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending',
    };
    await this.db.pendingSubmissions.add(entry);
    await this.refreshPendingCount();
  }

  async flushPendingSubmissions(): Promise<FlushPendingResult> {
    await this.initPromise;

    if (this.flushPromise) {
      return this.flushPromise;
    }

    const queued = await this.db.pendingSubmissions.count();
    this.publishProgress({
      total: queued,
      synced: 0,
      failed: 0,
      remaining: queued,
      phase: 'running',
    });

    if (!this.canSync()) {
      const remaining = queued;
      this.publishProgress({
        total: remaining,
        synced: 0,
        failed: 0,
        remaining,
        phase: 'complete',
      });
      return { synced: 0, failed: 0, remaining };
    }

    this.flushPromise = this.flushPendingSubmissionsInternal();
    try {
      return await this.flushPromise;
    } finally {
      this.flushPromise = null;
      this.pauseRequested = false;
    }
  }

  pauseFlush(): void {
    this.pauseRequested = true;
  }

  markFlushRunning(): void {
    this.pauseRequested = false;
    const remaining = this._pendingCount.value;
    this.publishProgress({
      total: remaining,
      synced: 0,
      failed: 0,
      remaining,
      phase: 'running',
    });
  }

  async multiplyPendingSubmissions(factor: number): Promise<number> {
    await this.initPromise;

    const clamped = Math.min(
      this.maxMultiplyFactor,
      Math.max(this.minMultiplyFactor, Math.floor(factor))
    );
    const pending = await this.db.pendingSubmissions.toArray();
    if (pending.length === 0) {
      return 0;
    }

    const now = Date.now();
    const clones: PendingSurveySubmission[] = [];

    for (const entry of pending) {
      for (let copy = 1; copy < clamped; copy++) {
        const eventUUID = this.createEventUUID();
        clones.push({
          eventUUID,
          payload: {
            ...entry.payload,
            eventUUID,
            answers: (entry.payload.answers || []).map((answer) => ({
              ...answer,
            })),
          },
          createdAt: now + copy,
          retryCount: 0,
          status: 'pending',
        });
      }
    }

    if (clones.length > 0) {
      await this.db.pendingSubmissions.bulkAdd(clones);
      await this.refreshPendingCount();
    }

    return clones.length;
  }

  async getPendingCount(): Promise<number> {
    await this.initPromise;
    return this.db.pendingSubmissions.count();
  }

  private async initializeQueue(): Promise<void> {
    await this.resetInFlightEntries();
    await this.refreshPendingCount();
  }

  private async resetInFlightEntries(): Promise<void> {
    const inFlight = await this.db.pendingSubmissions
      .where('status')
      .equals('in_flight')
      .toArray();

    for (const entry of inFlight) {
      if (entry.id === undefined) {
        continue;
      }
      await this.db.pendingSubmissions.update(entry.id, {
        status: 'pending',
      });
    }
  }

  private async flushPendingSubmissionsInternal(): Promise<FlushPendingResult> {
    let synced = 0;
    let failed = 0;
    let remaining = 0;
    let consecutiveFailures = 0;

    try {
      const pending = await this.db.pendingSubmissions
        .orderBy('createdAt')
        .toArray();
      const total = pending.length;
      remaining = total;
      this.publishProgress({
        total,
        synced,
        failed,
        remaining,
        phase: 'running',
      });

      for (let index = 0; index < pending.length; index++) {
        if (this.shouldStopFlush(consecutiveFailures)) {
          remaining = pending.length - index;
          break;
        }

        if (index > 0) {
          await this.delay(this.delayBetweenSubmissionsMs);
          if (this.shouldStopFlush(consecutiveFailures)) {
            remaining = pending.length - index;
            break;
          }
        }

        const entry = pending[index];
        const marked = await this.markInFlight(entry);
        if (!marked) {
          remaining = pending.length - index;
          break;
        }

        try {
          const response = await this.submitWithRetry(entry.payload);

          if (this.isSubmissionSuccessful(response)) {
            await this.markSynced(entry);
            synced++;
            consecutiveFailures = 0;
          } else {
            await this.markFailed(entry, 'Submission was not accepted');
            failed++;
            consecutiveFailures++;
          }
        } catch (error) {
          await this.markFailed(entry, this.toErrorMessage(error));
          failed++;
          consecutiveFailures++;
        }

        remaining = total - synced - failed;
        this.publishProgress({
          total,
          synced,
          failed,
          remaining,
          phase: 'running',
        });
        await this.refreshPendingCount();
      }

      const phase =
        this.pauseRequested && remaining > 0 ? 'paused' : 'complete';
      this.publishProgress({
        total,
        synced,
        failed,
        remaining,
        phase,
      });
    } finally {
      await this.refreshPendingCount();
      if (remaining === 0 && failed === 0) {
        this.recordLastSync();
      }
    }

    return { synced, failed, remaining };
  }

  private shouldStopFlush(consecutiveFailures: number): boolean {
    return (
      this.pauseRequested ||
      !this.canSync() ||
      consecutiveFailures >= this.maxConsecutiveFailures
    );
  }

  private publishProgress(progress: FlushProgress): void {
    this._flushProgress.next(progress);
  }

  private async markInFlight(entry: PendingSurveySubmission): Promise<boolean> {
    if (entry.id === undefined) {
      return false;
    }

    await this.db.pendingSubmissions.update(entry.id, {
      status: 'in_flight',
      lastAttemptAt: Date.now(),
    });
    return true;
  }

  private async markSynced(entry: PendingSurveySubmission): Promise<void> {
    if (!isKioskModeEnabled()) {
      await this.db.surveys.update(entry.eventUUID, { submitted: true });
      await this.db.progress.delete(entry.eventUUID);
    }
    if (entry.id !== undefined) {
      await this.db.pendingSubmissions.delete(entry.id);
    }
  }

  private async markFailed(
    entry: PendingSurveySubmission,
    lastError: string
  ): Promise<void> {
    if (entry.id === undefined) {
      return;
    }

    await this.db.pendingSubmissions.update(entry.id, {
      status: 'failed',
      retryCount: entry.retryCount + 1,
      lastError,
      lastAttemptAt: Date.now(),
    });
  }

  private async submitWithRetry(
    payload: SurveyAnswerResult
  ): Promise<unknown> {
    let lastError: unknown;

    for (let attempt = 0; attempt < this.maxRetriesPerItem; attempt++) {
      try {
        return await this.submitOne(payload);
      } catch (error) {
        lastError = error;

        if (
          !isTransientSubmitError(error) ||
          attempt === this.maxRetriesPerItem - 1
        ) {
          throw error;
        }

        await this.delay(this.retryBaseDelayMs * Math.pow(2, attempt));
      }
    }

    throw lastError;
  }

  private submitOne(payload: SurveyAnswerResult): Promise<unknown> {
    return this.httpHelperService.requestPromise(
      'POST',
      `${this.baseUrl}/submit-results`,
      CaseConverter.keysToSnakeCase({ ...payload, onlineSubmitted: true }),
      undefined,
      { timeoutMs: this.submitTimeoutMs }
    );
  }

  private isSubmissionSuccessful(response: unknown): boolean {
    if (!response || typeof response !== 'object') {
      return false;
    }

    const result = response as {
      submitted?: boolean;
      errorMessage?: string;
      response?: { submitted?: boolean; errorMessage?: string };
    };

    if (result.submitted) {
      return true;
    }

    if (result.errorMessage === 'ALREADY COMPLETED') {
      return true;
    }

    if (result.response?.submitted) {
      return true;
    }

    return result.response?.errorMessage === 'ALREADY COMPLETED';
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return error.statusText || `HTTP ${error.status}`;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return 'Submit failed';
  }

  private delay(ms: number): Promise<void> {
    if (ms <= 0) {
      return Promise.resolve();
    }
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private canSync(): boolean {
    return this.networkConnectivity.isOnline;
  }

  private async refreshPendingCount(): Promise<void> {
    const count = await this.db.pendingSubmissions.count();
    this._pendingCount.next(count);
  }

  private createEventUUID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `clone-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  private readLastSyncAt(): number | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const stored = localStorage.getItem(LAST_SYNC_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = Number(stored);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private recordLastSync(): void {
    const timestamp = Date.now();
    this._lastSyncAt.next(timestamp);

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(LAST_SYNC_STORAGE_KEY, String(timestamp));
    }
  }
}
